import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PackageCheck, PlusCircle, Search, User, Package, Calendar, List, Grid3x3, Printer, Eye, Trash2, Truck, Filter, BarChart2, ArrowLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import DataTable from '@/components/DataTable';
import ExportExcelButton from '@/components/ExportExcelButton';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import NuevoAlistamientoModal from './components/NuevoAlistamientoModal';
import SeleccionarClienteModal from './components/SeleccionarClienteModal';
import SeleccionarZonaModal from './components/SeleccionarZonaModal';
import PonerEnRutaModal from './components/PonerEnRutaModal';
import { getAlistamientosByLavanderia, getAlistamientoById, deleteAlistamiento } from './services/alistamientos.services';
import { getEstadoBadge, alistamientosDataColumns, alistamientosExcelColumns, expandAlistamientosPorItem } from './utils/alistamientosColumns';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const AlistamientosPage = () => {
  const { idLavanderia } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [alistamientosPendientes, setAlistamientosPendientes] = useState([]);
  const [alistamientosFinalizados, setAlistamientosFinalizados] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
  const [isZonaModalOpen, setIsZonaModalOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedZona, setSelectedZona] = useState(null);
  const [selectedAlistamiento, setSelectedAlistamiento] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isPrinting, setIsPrinting] = useState(null);
  const [isDetalleBolsasOpen, setIsDetalleBolsasOpen] = useState(false);
  const [alistamientoDetalle, setAlistamientoDetalle] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [alistamientoToDelete, setAlistamientoToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isPonerEnRutaOpen, setIsPonerEnRutaOpen] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState('');
  const [clienteFilter, setClienteFilter] = useState('');

  const printer = getPrinterInstance();

  const loadAlistamientosPendientes = useCallback(async () => {
    try {
      const response = await getAlistamientosByLavanderia(idLavanderia, {
        query: { 
          estado: 'Pendiente'
        },
        page: 1,
        limit: 1000,
      });
      setAlistamientosPendientes(response.data || []);
    } catch (error) {
      console.error('Error al cargar alistamientos pendientes:', error);
    }
  }, [idLavanderia]);

  const loadAlistamientosFinalizados = useCallback(async () => {
    setIsLoading(true);
    try {
      // Transformar filtros del drawer: convertir texto plano a regex para campos de texto
      const query = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (!value) return;
        const field = filterFields.find(f => f.id === key);
        if (field && (field.type === 'text') && typeof value === 'string') {
          query[key] = { $regex: value, $options: 'i' };
        } else {
          query[key] = value;
        }
      });

      if (searchTerm) {
        query.$or = [
          { 'cliente.nombre': { $regex: searchTerm, $options: 'i' } },
          { consecutivo: { $regex: searchTerm, $options: 'i' } },
        ];
      }
      // Filtro rápido por cliente
      if (clienteFilter) {
        query['cliente.nombre'] = { $regex: clienteFilter, $options: 'i' };
      }
      // Filtro por estado (quick filter sobreescribe drawer si ambos están)
      if (estadoFilter) {
        query.estado = estadoFilter;
      } else if (!query.estado) {
        // Excluir pendientes y eliminados
        query.estado = { $nin: ['Pendiente', 'Eliminado'] };
      }

      const response = await getAlistamientosByLavanderia(idLavanderia, {
        query,
        page: currentPage,
        limit: itemsPerPage,
      });

      setAlistamientosFinalizados(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los alistamientos',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [idLavanderia, currentPage, itemsPerPage, filters, searchTerm, estadoFilter, clienteFilter, toast]);

  useEffect(() => {
    loadAlistamientosPendientes();
  }, [loadAlistamientosPendientes]);

  useEffect(() => {
    loadAlistamientosFinalizados();
  }, [loadAlistamientosFinalizados]);


  const handleNuevoAlistamiento = () => {
    // Primero seleccionar zona, luego cliente
    setIsZonaModalOpen(true);
  };

  const handleZonaSeleccionada = (zona) => {
    setSelectedZona(zona);
    setIsZonaModalOpen(false);
    setIsClienteModalOpen(true);
  };

  const handleClienteSeleccionado = (cliente) => {
    setSelectedCliente(cliente);
    setIsClienteModalOpen(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Limpiar estados para garantizar que el próximo alistamiento cargue correctamente
    setSelectedAlistamiento(null);
    setSelectedCliente(null);
    setSelectedZona(null);
  };

  const handleCloseClienteModal = () => {
    setIsClienteModalOpen(false);
    setSelectedZona(null);
  };

  const handleCloseZonaModal = () => {
    setIsZonaModalOpen(false);
    setSelectedZona(null);
  };

  const handleSuccess = () => {
    toast({
      title: 'Éxito',
      description: 'Alistamiento guardado correctamente',
    });
    loadAlistamientosPendientes();
    loadAlistamientosFinalizados();
    setIsModalOpen(false);
    setSelectedCliente(null);
    setSelectedZona(null);
  };

  const handleEditarAlistamiento = async (alistamiento) => {
    try {
      // Buscar el alistamiento actualizado en la base de datos
      toast({
        title: 'Cargando...',
        description: 'Obteniendo datos actualizados del alistamiento',
      });

      const response = await getAlistamientoById(alistamiento._id);
      const alistamientoActualizado = response.data || response;

      setSelectedAlistamiento(alistamientoActualizado);

      // Asegurar que el cliente tenga la estructura correcta
      const cliente = alistamientoActualizado.cliente;
      if (cliente && !cliente._id && cliente.id) {
        // Si tiene 'id' en lugar de '_id', convertir
        cliente._id = cliente.id;
      }

      setSelectedCliente(cliente);
      setSelectedZona(alistamientoActualizado.zona); // Cargar zona del alistamiento
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error al cargar alistamiento:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el alistamiento. Intenta de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleEliminarAlistamiento = (alistamiento) => {
    setAlistamientoToDelete(alistamiento);
    setIsDeleteDialogOpen(true);
  };


  const confirmDeleteAlistamiento = async () => {
    if (!alistamientoToDelete) return;

    setIsDeleting(true);
    try {
      await deleteAlistamiento(alistamientoToDelete._id);

      toast({
        title: 'Éxito',
        description: `Alistamiento ${alistamientoToDelete.consecutivo} eliminado correctamente`,
      });

      loadAlistamientosPendientes();
      loadAlistamientosFinalizados();
      setIsDeleteDialogOpen(false);
      setAlistamientoToDelete(null);
    } catch (error) {
      console.error('Error al eliminar alistamiento:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el alistamiento',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImprimirConsolidado = async (alistamiento) => {
    if (!printer.isBluetoothAvailable()) {
      toast({
        title: 'No disponible',
        description: 'Tu navegador no soporta Bluetooth',
        variant: 'destructive',
      });
      return;
    }

    setIsPrinting(alistamiento._id);

    try {
      if (!printer.isConnected()) {
        toast({
          title: 'Conectando...',
          description: 'Selecciona tu impresora Bluetooth',
        });
        await printer.connect();
      }

      toast({
        title: 'Imprimiendo...',
        description: 'Imprimiendo consolidados',
      });

      // Imprimir consolidado de bolsas primero
      await printer.printConsolidadoBolsas(
        alistamiento.bolsas || [],
        alistamiento.cliente,
        alistamiento.alistador,
        alistamiento.consecutivo,
        alistamiento.items || []
      );

      // Pequeño delay antes del consolidado de items
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Imprimir consolidado de items
      await printer.printConsolidadoAlistamiento(alistamiento);

      toast({
        title: 'Éxito',
        description: `Consolidados de ${alistamiento.consecutivo} impresos correctamente`,
      });

    } catch (error) {
      console.error('Error al imprimir consolidado:', error);

      let errorMessage = 'No se pudo imprimir el consolidado';
      if (error.message.includes('User cancelled')) {
        errorMessage = 'Impresión cancelada';
      } else if (error.message.includes('No hay conexión')) {
        errorMessage = 'Impresora desconectada. Intenta de nuevo.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsPrinting(null);
    }
  };

  const handleImprimirBolsa = async (bolsa) => {
    if (!printer.isBluetoothAvailable()) {
      toast({
        title: 'No disponible',
        description: 'Tu navegador no soporta Bluetooth',
        variant: 'destructive',
      });
      return;
    }

    setIsPrinting(bolsa.numero);

    try {
      if (!printer.isConnected()) {
        toast({
          title: 'Conectando...',
          description: 'Selecciona tu impresora Bluetooth',
        });
        await printer.connect();
      }

      await printer.printBolsaResumen(
        bolsa,
        alistamientoDetalle.cliente,
        alistamientoDetalle.alistador,
        alistamientoDetalle.consecutivo
      );

      toast({
        title: 'Éxito',
        description: `Bolsa #${bolsa.numero} impresa correctamente`,
      });

    } catch (error) {
      console.error('Error al imprimir bolsa:', error);

      let errorMessage = 'No se pudo imprimir la bolsa';
      if (error.message.includes('User cancelled')) {
        errorMessage = 'Impresión cancelada';
      } else if (error.message.includes('No hay conexión')) {
        errorMessage = 'Impresora desconectada. Intenta de nuevo.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsPrinting(null);
    }
  };

  // Columnas para la tabla
  const tableColumns = [
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'view',
          icon: 'Eye',
          tooltip: 'Ver detalle de bolsas',
          className: 'text-blue-600 hover:text-blue-700',
        },
        {
          key: 'print',
          icon: 'Printer',
          tooltip: 'Imprimir consolidado',
          className: 'text-purple-600 hover:text-purple-700',
        },
        {
          key: 'delete',
          icon: 'Trash2',
          tooltip: 'Eliminar alistamiento',
          className: 'text-red-600 hover:text-red-700',
          condition: (row) => user?.role === 'admin' || user?.role === 'supervisor',
        },
      ],
    },
    ...alistamientosDataColumns,
  ];

  // Columnas para Excel (una fila por item)
  const excelColumns = alistamientosExcelColumns;

  // Campos de filtro
  const filterFields = [
    {
      id: 'cliente.nombre',
      label: 'Cliente',
      type: 'text',
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'Alistado', label: 'Alistado' },
        { value: 'En Ruta', label: 'En Ruta' },
        { value: 'Entregado', label: 'Entregado' },
        { value: 'Cancelado', label: 'Cancelado' },
      ],
    },
    {
      id: 'fechaCreacion',
      label: 'Rango de Fecha',
      filterType: 'daterange',
    },
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // Servicio para exportar (una fila por item)
  const exportService = async () => {
    const fullQuery = { ...filters, estado: { $ne: 'Pendiente' } };
    const response = await getAlistamientosByLavanderia(idLavanderia, {
      query: fullQuery,
      limit: 10000,
      page: 1,
    });
    // Expandir: una fila por cada item del alistamiento
    return expandAlistamientosPorItem(response.data || []);
  };

  // Manejador de acciones de la tabla
  const handleTableAction = (action, row) => {
    if (action === 'print') {
      handleImprimirConsolidado(row);
    } else if (action === 'view') {
      setAlistamientoDetalle(row);
      setIsDetalleBolsasOpen(true);
    } else if (action === 'delete') {
      handleEliminarAlistamiento(row);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6 lg:p-8"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-purple-500/10 p-3 rounded-lg">
              <PackageCheck className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <CardTitle className="text-2xl">Alistamientos</CardTitle>
              <CardDescription>
                Gestiona los alistamientos de prendas para entrega
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => navigate(`/centros-lavado/${idLavanderia}/reportes-alistamientos`)}
              className="gap-2"
            >
              <BarChart2 className="h-4 w-4" />
              Reportes
            </Button>
            <Button onClick={handleNuevoAlistamiento} className="gap-2">
              <PlusCircle className="h-4 w-4" />
              Alistar Prendas
            </Button>
          </div>
        </CardHeader>
          <CardContent>
            {/* Sección de Pendientes */}
            <div className="mb-8">
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Buscar por cliente o consecutivo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : alistamientosPendientes.filter(a =>
                !searchTerm ||
                a.cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                a.consecutivo?.toLowerCase().includes(searchTerm.toLowerCase())
              ).length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <PackageCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">
                      {searchTerm ? 'No se encontraron alistamientos' : 'No hay alistamientos registrados'}
                    </p>
                    <Button onClick={handleNuevoAlistamiento} className="gap-2">
                      <PlusCircle className="h-4 w-4" />
                      Crear Primer Alistamiento
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {alistamientosPendientes
                    .filter(a =>
                      !searchTerm ||
                      a.cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      a.consecutivo?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((alistamiento) => (
                      <motion.div
                        key={alistamiento._id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Card className={`hover:shadow-lg transition-shadow ${alistamiento.zona === 'Toallas'
                            ? 'border-l-4 border-l-blue-400 bg-blue-800/30'
                            : 'border-l-4 border-l-purple-400 bg-purple-800/30'
                          }`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-sm">{alistamiento.consecutivo}</h3>
                                  <Badge className={`${alistamiento.zona === 'Toallas'
                                      ? 'bg-blue-500 text-blue-1000'
                                      : 'bg-purple-500 text-purple-1000'
                                    }`}>
                                    {alistamiento.zona}
                                  </Badge>
                                  <Badge className={getEstadoBadge(alistamiento.estado)}>
                                    {alistamiento.estado}
                                  </Badge>
                                </div>
                                <p className="text-sm font-medium text-gray-700">{alistamiento.cliente.nombre}</p>
                                <p className="text-xs text-gray-500 mt-1">{alistamiento.cliente.direccion || 'Sin dirección'}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                              <div className="flex items-center gap-1">
                                <Package className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-600">{alistamiento.bolsas?.length || 0} bolsas</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-gray-400" />
                                <span className="text-gray-600">{alistamiento.totalItems} items</span>
                              </div>
                            </div>

                            <div className="text-xs text-gray-500 mb-3">
                              <p>Alistador: {alistamiento.alistador.nombre}</p>
                              <p>Peso total: {alistamiento.pesoTotal}g</p>
                            </div>

                            <div className="flex gap-2">
                              {alistamiento.estado !== 'Entregado' && (
                                <Button
                                  onClick={() => handleEditarAlistamiento(alistamiento)}
                                  variant="outline"
                                  className="flex-1 gap-2"
                                  size="sm"
                                >
                                  <Package className="h-4 w-4" />
                                  Continuar Alistamiento
                                </Button>
                              )}
                              <Button
                                onClick={() => handleEliminarAlistamiento(alistamiento)}
                                variant="outline"
                                className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                                size="sm"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                </div>
              )}
            </div>

            {/* Sección de Finalizados */}
            <div className="mt-8 pt-8 border-t">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <List className="h-5 w-5 text-purple-600" />
                  Alistamientos Finalizados ({totalRecords})
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsPonerEnRutaOpen(true)}
                    className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Truck className="h-4 w-4" />
                    Poner en Ruta
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsFilterDrawerOpen(true)}
                    className="gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filtros
                  </Button>
                  <ExportExcelButton
                    service={exportService}
                    columns={excelColumns}
                    fileName={`alistamientos_finalizados_${new Date().toISOString().split('T')[0]}`}
                  />
                </div>
              </div>

              {/* Filtros rápidos */}
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Filtro rápido por nombre de cliente */}
                <div className="relative flex-1 max-w-sm">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Filtrar por cliente..."
                    value={clienteFilter}
                    onChange={(e) => { setClienteFilter(e.target.value); setCurrentPage(1); }}
                    className="pl-10"
                  />
                </div>
                {/* Filtro rápido por estado */}
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="text-sm text-gray-500 font-medium">Estado:</span>
                  {[
                    { value: '', label: 'Todos', color: 'bg-gray-100 text-gray-800' },
                    { value: 'Alistado', label: 'Alistado', color: 'bg-purple-100 text-purple-800' },
                    { value: 'En Ruta', label: 'En Ruta', color: 'bg-blue-100 text-blue-800' },
                    { value: 'Entregado', label: 'Entregado', color: 'bg-green-100 text-green-800' },
/*                     { value: 'Cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-800' },
 */                  ].map((est) => (
                    <button
                      key={est.value}
                      onClick={() => { setEstadoFilter(est.value); setCurrentPage(1); }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all border ${
                        estadoFilter === est.value
                          ? `${est.color} ring-2 ring-offset-1 ring-purple-400 border-transparent`
                          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {est.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtros Aplicados */}
              <AppliedFilters
                filters={filters}
                fields={filterFields}
                onRemoveFilter={handleRemoveFilter}
              />

              <DataTable
                data={alistamientosFinalizados}
                columns={tableColumns}
                isLoading={isLoading}
                page={currentPage}
                limit={itemsPerPage}
                totalRecords={totalRecords}
                onPageChange={setCurrentPage}
                onLimitChange={setItemsPerPage}
                onAction={handleTableAction}
              />
            </div>
          </CardContent>
        </Card>

      {/* Modal de Detalle de Bolsas */}
      <Dialog open={isDetalleBolsasOpen} onOpenChange={setIsDetalleBolsasOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Detalle de Bolsas - {alistamientoDetalle?.consecutivo || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Cliente: {alistamientoDetalle?.cliente?.nombre || 'N/A'} |
              Alistador: {alistamientoDetalle?.alistador?.nombre || 'N/A'} |
              Total: {alistamientoDetalle?.totalItems || 0} items - {alistamientoDetalle?.pesoTotal || 0}g ({((alistamientoDetalle?.pesoTotal || 0) / 1000).toFixed(2)} Kg)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!alistamientoDetalle?.bolsas || alistamientoDetalle.bolsas.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Este alistamiento no tiene bolsas registradas</p>
                </CardContent>
              </Card>
            ) : (
              // Deduplicar bolsas por numero (el backend puede generar duplicados al agregar items al mismo numero)
              Object.values(
                alistamientoDetalle.bolsas.reduce((acc, bolsa) => {
                  if (acc[bolsa.numero]) {
                    acc[bolsa.numero].totalItems += bolsa.totalItems;
                    acc[bolsa.numero].pesoTotal += bolsa.pesoTotal;
                  } else {
                    acc[bolsa.numero] = { ...bolsa };
                  }
                  return acc;
                }, {})
              ).map((bolsa) => {
                // Filtrar items que pertenecen a esta bolsa y deduplicar por prendaId
                const itemsDeBolsa = Object.values(
                  (alistamientoDetalle.items?.filter(item => item.bolsa === bolsa.numero) || [])
                    .reduce((acc, item) => {
                      if (!acc[item.prendaId]) acc[item.prendaId] = item;
                      return acc;
                    }, {})
                );

                return (
                  <Card key={bolsa.numero} className="border-2">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Package className="h-5 w-5 text-purple-600" />
                          <h4 className="font-semibold">Bolsa #{bolsa.numero}</h4>
                          <Badge variant="secondary">{bolsa.totalItems} items</Badge>
                          <Badge variant="secondary">{bolsa.pesoTotal}g - {(bolsa.pesoTotal / 1000).toFixed(2)} Kg</Badge>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Crear objeto bolsa con items para imprimir
                            const bolsaConItems = {
                              ...bolsa,
                              items: itemsDeBolsa
                            };
                            handleImprimirBolsa(bolsaConItems);
                          }}
                          disabled={isPrinting === bolsa.numero}
                          className="h-8 gap-2"
                        >
                          <Printer className="h-4 w-4" />
                          {isPrinting === bolsa.numero ? 'Imprimiendo...' : 'Imprimir'}
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {itemsDeBolsa.length > 0 ? (
                          itemsDeBolsa.map((item, idx) => (
                            <div key={idx} className="text-sm bg-gray-50 p-2 rounded">
                              <div className="font-medium truncate">{item.nombre}</div>
                              <div className="text-xs text-gray-500">Cantidad: {item.cantidad}</div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 col-span-full">No hay items en esta bolsa</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Selección de Zona */}
      <SeleccionarZonaModal
        isOpen={isZonaModalOpen}
        onClose={handleCloseZonaModal}
        onZonaSeleccionada={handleZonaSeleccionada}
      />

      {/* Modal de Selección de Cliente */}
      <SeleccionarClienteModal
        isOpen={isClienteModalOpen}
        onClose={handleCloseClienteModal}
        lavanderiaId={idLavanderia}
        onClienteSeleccionado={handleClienteSeleccionado}
      />

      {/* Modal de Nuevo Alistamiento */}
      <NuevoAlistamientoModal
        key={selectedAlistamiento?._id || selectedCliente?._id || 'nuevo'}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        cliente={selectedCliente}
        zona={selectedZona}
        alistador={user}
        lavanderiaId={idLavanderia}
        alistamiento={selectedAlistamiento}
        onSuccess={handleSuccess}
      />

      {/* Modal Poner en Ruta */}
      <PonerEnRutaModal
        isOpen={isPonerEnRutaOpen}
        onClose={() => setIsPonerEnRutaOpen(false)}
        idLavanderia={idLavanderia}
        onSuccess={() => {
          loadAlistamientosPendientes();
          loadAlistamientosFinalizados();
        }}
      />

      {/* Modal de Confirmación de Eliminación */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Confirmar Eliminación
            </DialogTitle>
            <DialogDescription className="space-y-3 pt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 font-semibold mb-2">
                  ⚠️ ADVERTENCIA
                </p>
                <p className="text-amber-700 text-sm">
                  El alistamiento será marcado como eliminado y dejará de aparecer en las vistas normales. Los datos se conservarán en el sistema.
                </p>
              </div>

              {alistamientoToDelete && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Consecutivo:</span>
                    <span className="text-sm font-semibold text-purple-600">
                      {alistamientoToDelete.consecutivo}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Cliente:</span>
                    <span className="text-sm font-semibold">
                      {alistamientoToDelete.cliente?.nombre}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Zona:</span>
                    <span className="text-sm font-semibold">
                      {alistamientoToDelete.zona}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Bolsas:</span>
                    <span className="text-sm font-semibold">
                      {alistamientoToDelete.bolsas?.length || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Total Items:</span>
                    <span className="text-sm font-semibold">
                      {alistamientoToDelete.totalItems}
                    </span>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-600 pt-2">
                ¿Estás seguro de que deseas marcar este alistamiento como eliminado?
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setAlistamientoToDelete(null);
              }}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteAlistamiento}
              disabled={isDeleting}
              className="flex-1 gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Eliminar Alistamiento
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* FilterDrawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApply={handleApplyFilters}
      />
    </motion.div>
  );
};

export default AlistamientosPage;
